import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Trash2, Settings as SettingsIcon, Palette, FileText, ArrowLeft, ImageIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminSettings() {
  const navigate = useNavigate();
  
  // Theme settings states
  const [themeSettings, setThemeSettings] = useState({
    theme_primary_color: '#0F766E',
    theme_secondary_color: '#D97706',
    theme_accent_color: '#0F766E'
  });
  const [savingTheme, setSavingTheme] = useState(false);

  // Delete buttons setting
  const [showDeleteButtons, setShowDeleteButtons] = useState(false);
  const [savingDeleteSetting, setSavingDeleteSetting] = useState(false);

  // Page visibility settings
  const [pageVisibility, setPageVisibility] = useState({
    show_about_us: true,
    show_careers: true,
    show_faq: true,
    show_contact_us: true
  });
  const [savingPageVisibility, setSavingPageVisibility] = useState(false);

  // Page content states
  const [pageContents, setPageContents] = useState({
    about_us: '',
    contact_us: '',
    faq: '',
    careers: ''
  });
  const [selectedPage, setSelectedPage] = useState('about_us');
  const [savingPage, setSavingPage] = useState(false);

  useEffect(() => {
    fetchThemeSettings();
    fetchPageContents();
  }, []);

  const fetchThemeSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setThemeSettings({
        theme_primary_color: response.data.theme_primary_color || '#0F766E',
        theme_secondary_color: response.data.theme_secondary_color || '#D97706',
        theme_accent_color: response.data.theme_accent_color || '#0F766E'
      });
      setShowDeleteButtons(response.data.show_delete_buttons || false);
      
      // Load page visibility settings
      setPageVisibility({
        show_about_us: response.data.show_about_us !== false,
        show_careers: response.data.show_careers !== false,
        show_faq: response.data.show_faq !== false,
        show_contact_us: response.data.show_contact_us !== false
      });
    } catch (error) {
      console.error('Error fetching theme settings:', error);
    }
  };

  const handleUpdateTheme = async () => {
    setSavingTheme(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/settings/theme?primary_color=${encodeURIComponent(themeSettings.theme_primary_color)}&secondary_color=${encodeURIComponent(themeSettings.theme_secondary_color)}&accent_color=${encodeURIComponent(themeSettings.theme_accent_color)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Theme settings saved! Refresh the page to see changes.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save theme settings');
    } finally {
      setSavingTheme(false);
    }
  };

  const handleToggleDeleteButtons = async (enabled) => {
    setSavingDeleteSetting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/settings/delete-buttons?enabled=${enabled}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowDeleteButtons(enabled);
      toast.success(enabled ? 'Delete buttons enabled' : 'Delete buttons disabled');
    } catch (error) {
      toast.error('Failed to update setting');
    } finally {
      setSavingDeleteSetting(false);
    }
  };

  const handleTogglePageVisibility = async (page) => {
    setSavingPageVisibility(true);
    try {
      const token = localStorage.getItem('token');
      const newValue = !pageVisibility[page];
      
      await axios.post(
        `${API}/admin/settings/page-visibility`,
        { page, visible: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPageVisibility({ ...pageVisibility, [page]: newValue });
      
      const pageName = page.replace('show_', '').replace(/_/g, ' ').toUpperCase();
      toast.success(`${pageName} ${newValue ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update page visibility');
    } finally {
      setSavingPageVisibility(false);
    }
  };

  const fetchPageContents = async () => {
    try {
      const pages = ['about_us', 'contact_us', 'faq', 'careers'];
      const contents = {};
      
      for (const page of pages) {
        const response = await axios.get(`${API}/pages/${page}`);
        contents[page] = response.data.content;
      }
      
      setPageContents(contents);
    } catch (error) {
      console.error('Error fetching page contents:', error);
    }
  };

  const handleSavePageContent = async () => {
    setSavingPage(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/pages/${selectedPage}?content=${encodeURIComponent(pageContents[selectedPage])}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Page content updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update page content');
    } finally {
      setSavingPage(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 py-8 sm:py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/admin/dashboard')}
            variant="ghost"
            className="mb-4 -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
              <SettingsIcon className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="font-playfair text-3xl sm:text-4xl font-bold text-primary">
                Admin Settings
              </h1>
              <p className="font-outfit text-gray-600">
                Manage app configuration and content
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Danger Zone - Delete Buttons */}
          <Card className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <Trash2 className="w-6 h-6 text-red-600" />
              <h2 className="font-playfair text-2xl font-bold text-red-600">Danger Zone</h2>
            </div>
            
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Label className="font-semibold text-gray-800 cursor-pointer">Enable Delete Buttons</Label>
                  </div>
                  <p className="text-sm text-gray-600">
                    When enabled, delete buttons will appear for customers, businesses, and events throughout the admin dashboard. 
                    Deletion is permanent and cannot be undone.
                  </p>
                </div>
                <div className="ml-4 flex-shrink-0">
                  <Switch
                    checked={showDeleteButtons}
                    onCheckedChange={handleToggleDeleteButtons}
                    disabled={savingDeleteSetting}
                  />
                </div>
              </div>
              {showDeleteButtons && (
                <div className="mt-3 flex items-center gap-2 text-sm text-red-700 bg-red-100 p-2 rounded">
                  <span className="font-semibold">⚠️ Warning:</span>
                  <span>Delete buttons are now visible. Use with caution!</span>
                </div>
              )}
            </div>
          </Card>

          {/* Page Visibility Settings */}
          <Card className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-6 h-6 text-primary" />
              <h2 className="font-playfair text-2xl font-bold text-primary">Page Visibility</h2>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Control which pages are visible to users in navigation menus
            </p>

            <div className="space-y-4">
              {/* About Us Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex-1">
                  <Label htmlFor="show-about-us" className="font-semibold text-gray-800 cursor-pointer">About Us Page</Label>
                  <p className="text-sm text-gray-600 mt-1">Show/Hide About Us in navigation</p>
                </div>
                <Switch
                  id="show-about-us"
                  checked={pageVisibility.show_about_us}
                  onCheckedChange={() => handleTogglePageVisibility('show_about_us')}
                  disabled={savingPageVisibility}
                />
              </div>

              {/* Careers Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex-1">
                  <Label htmlFor="show-careers" className="font-semibold text-gray-800 cursor-pointer">Careers Page</Label>
                  <p className="text-sm text-gray-600 mt-1">Show/Hide Careers in navigation</p>
                </div>
                <Switch
                  id="show-careers"
                  checked={pageVisibility.show_careers}
                  onCheckedChange={() => handleTogglePageVisibility('show_careers')}
                  disabled={savingPageVisibility}
                />
              </div>

              {/* FAQ Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex-1">
                  <Label htmlFor="show-faq" className="font-semibold text-gray-800 cursor-pointer">FAQ Page</Label>
                  <p className="text-sm text-gray-600 mt-1">Show/Hide FAQ in navigation</p>
                </div>
                <Switch
                  id="show-faq"
                  checked={pageVisibility.show_faq}
                  onCheckedChange={() => handleTogglePageVisibility('show_faq')}
                  disabled={savingPageVisibility}
                />
              </div>

              {/* Contact Us Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex-1">
                  <Label htmlFor="show-contact-us" className="font-semibold text-gray-800 cursor-pointer">Contact Us Page</Label>
                  <p className="text-sm text-gray-600 mt-1">Show/Hide Contact Us in navigation</p>
                </div>
                <Switch
                  id="show-contact-us"
                  checked={pageVisibility.show_contact_us}
                  onCheckedChange={() => handleTogglePageVisibility('show_contact_us')}
                  disabled={savingPageVisibility}
                />
              </div>
            </div>
          </Card>

          {/* Theme Settings */}
          <Card className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <Palette className="w-6 h-6 text-primary" />
              <h2 className="font-playfair text-2xl font-bold text-primary">App Theme</h2>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p className="text-sm text-gray-600">
                Customize your app's color theme. Changes will apply across the entire application after refresh.
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              {/* Primary Color */}
              <div>
                <Label className="font-semibold text-gray-700 mb-3 block">Primary Color</Label>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={themeSettings.theme_primary_color}
                      onChange={(e) => setThemeSettings({ ...themeSettings, theme_primary_color: e.target.value })}
                      className="w-16 h-16 rounded-lg cursor-pointer border-2 border-gray-300"
                    />
                    <div className="flex-1">
                      <Input
                        type="text"
                        value={themeSettings.theme_primary_color}
                        onChange={(e) => setThemeSettings({ ...themeSettings, theme_primary_color: e.target.value })}
                        placeholder="#0F766E"
                        className="font-mono"
                      />
                      <p className="text-xs text-gray-500 mt-1">Headers, nav, buttons</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Secondary Color */}
              <div>
                <Label className="font-semibold text-gray-700 mb-3 block">Secondary Color</Label>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={themeSettings.theme_secondary_color}
                      onChange={(e) => setThemeSettings({ ...themeSettings, theme_secondary_color: e.target.value })}
                      className="w-16 h-16 rounded-lg cursor-pointer border-2 border-gray-300"
                    />
                    <div className="flex-1">
                      <Input
                        type="text"
                        value={themeSettings.theme_secondary_color}
                        onChange={(e) => setThemeSettings({ ...themeSettings, theme_secondary_color: e.target.value })}
                        placeholder="#D97706"
                        className="font-mono"
                      />
                      <p className="text-xs text-gray-500 mt-1">Accents, highlights</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Accent Color */}
              <div>
                <Label className="font-semibold text-gray-700 mb-3 block">Accent Color</Label>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={themeSettings.theme_accent_color}
                      onChange={(e) => setThemeSettings({ ...themeSettings, theme_accent_color: e.target.value })}
                      className="w-16 h-16 rounded-lg cursor-pointer border-2 border-gray-300"
                    />
                    <div className="flex-1">
                      <Input
                        type="text"
                        value={themeSettings.theme_accent_color}
                        onChange={(e) => setThemeSettings({ ...themeSettings, theme_accent_color: e.target.value })}
                        placeholder="#0F766E"
                        className="font-mono"
                      />
                      <p className="text-xs text-gray-500 mt-1">Links, badges</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Color Presets */}
            <div className="mb-6">
              <Label className="font-semibold text-gray-700 mb-3 block">Quick Themes</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Button
                  variant="outline"
                  onClick={() => setThemeSettings({
                    theme_primary_color: '#0F766E',
                    theme_secondary_color: '#D97706',
                    theme_accent_color: '#0F766E'
                  })}
                  className="h-auto py-3 flex-col gap-2"
                >
                  <div className="flex gap-1">
                    <div className="w-6 h-6 rounded" style={{ backgroundColor: '#0F766E' }}></div>
                    <div className="w-6 h-6 rounded" style={{ backgroundColor: '#D97706' }}></div>
                  </div>
                  <span className="text-xs">Teal & Amber</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setThemeSettings({
                    theme_primary_color: '#1E40AF',
                    theme_secondary_color: '#DC2626',
                    theme_accent_color: '#1E40AF'
                  })}
                  className="h-auto py-3 flex-col gap-2"
                >
                  <div className="flex gap-1">
                    <div className="w-6 h-6 rounded" style={{ backgroundColor: '#1E40AF' }}></div>
                    <div className="w-6 h-6 rounded" style={{ backgroundColor: '#DC2626' }}></div>
                  </div>
                  <span className="text-xs">Blue & Red</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setThemeSettings({
                    theme_primary_color: '#7C3AED',
                    theme_secondary_color: '#EC4899',
                    theme_accent_color: '#7C3AED'
                  })}
                  className="h-auto py-3 flex-col gap-2"
                >
                  <div className="flex gap-1">
                    <div className="w-6 h-6 rounded" style={{ backgroundColor: '#7C3AED' }}></div>
                    <div className="w-6 h-6 rounded" style={{ backgroundColor: '#EC4899' }}></div>
                  </div>
                  <span className="text-xs">Purple & Pink</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setThemeSettings({
                    theme_primary_color: '#047857',
                    theme_secondary_color: '#F59E0B',
                    theme_accent_color: '#047857'
                  })}
                  className="h-auto py-3 flex-col gap-2"
                >
                  <div className="flex gap-1">
                    <div className="w-6 h-6 rounded" style={{ backgroundColor: '#047857' }}></div>
                    <div className="w-6 h-6 rounded" style={{ backgroundColor: '#F59E0B' }}></div>
                  </div>
                  <span className="text-xs">Green & Orange</span>
                </Button>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                onClick={handleUpdateTheme}
                disabled={savingTheme}
                className="flex-1 bg-primary hover:bg-primary/90"
              >
                {savingTheme ? 'Saving...' : 'Save Theme Settings'}
              </Button>
              <Button
                variant="outline"
                onClick={fetchThemeSettings}
                className="flex-1"
              >
                Reset to Saved
              </Button>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg text-sm text-blue-800 mt-4">
              <strong>Note:</strong> After saving, refresh the page to see theme changes across the app.
            </div>
          </Card>

          {/* Page Content Management */}
          <Card className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-6 h-6 text-primary" />
              <h2 className="font-playfair text-2xl font-bold text-primary">Manage About Pages</h2>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <p className="text-sm text-gray-600">
                Edit the content for About Us, Contact Us, FAQ, and Careers pages. Content supports Markdown formatting.
              </p>
            </div>

            {/* Page Selector */}
            <div className="mb-4">
              <Label className="font-semibold text-gray-700 mb-2 block">Select Page</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { key: 'about_us', label: 'About Us' },
                  { key: 'contact_us', label: 'Contact Us' },
                  { key: 'faq', label: 'FAQ' },
                  { key: 'careers', label: 'Careers' }
                ].map((page) => (
                  <Button
                    key={page.key}
                    variant={selectedPage === page.key ? 'default' : 'outline'}
                    onClick={() => setSelectedPage(page.key)}
                    className="w-full"
                  >
                    {page.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Content Editor */}
            <div className="mb-4">
              <Label className="font-semibold text-gray-700 mb-2 block">
                Page Content ({selectedPage.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')})
              </Label>
              <Textarea
                value={pageContents[selectedPage]}
                onChange={(e) => setPageContents({ ...pageContents, [selectedPage]: e.target.value })}
                rows={15}
                className="font-mono text-sm"
                placeholder="Enter page content here... (Markdown supported)"
              />
              <p className="text-xs text-gray-500 mt-2">
                💡 Tip: Use Markdown formatting (# for headings, ** for bold, * for italic, etc.)
              </p>
            </div>

            {/* Save Button */}
            <div className="flex gap-3">
              <Button
                onClick={handleSavePageContent}
                disabled={savingPage}
                className="flex-1 bg-primary hover:bg-primary/90"
              >
                {savingPage ? 'Saving...' : `Save ${selectedPage.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')} Page`}
              </Button>
              <Button
                variant="outline"
                onClick={fetchPageContents}
                className="flex-1"
              >
                Reset to Saved
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
